// Carrega snapshot do catálogo do backend e mede banda durante o download
// (fusão de duas operações em uma — todo byte baixado serve a 2 propósitos).
//
// Decisão de fluxo (Fase 3.7):
// - Tem snapshot fresco (<24h) no IndexedDB? Usa ele + reusa bandwidth persistida
// - Senão, baixa do backend (envia If-None-Match se tem hash anterior),
//   mede banda pelos bytes/tempo, grava no IndexedDB
//
// Esta função substitui o bandwidth probe separado (measureBandwidth) no
// caminho normal de login. Probe externo só roda em casos edge (sem provider).

import { getCapability } from '@app-iptv/device'

import { getApiBase, swipeAuthHeader } from './api'
import { curarCotaSePreciso } from './catalog-auto-cura'
import { catalogStore, SNAPSHOT_DEFAULT_TTL_MS, SURFACE_NAMES } from './catalog-store'
import type {
  CatalogSnapshot,
  SnapshotMeta,
  SnapshotRecord,
  SnapshotWriteResult,
  SurfaceName,
} from './catalog-store'
import {
  catalogWriteMode,
  modoDaTentativaManual,
  noteWriteResult,
  registrarResultadoManual,
  registrarTentativa,
  surfacesFor,
  ultimaTentativa,
} from './catalog-write-breaker'
import type { CatalogWriteMode } from './catalog-write-breaker'
import { makeNetDeadline, orFallbackAfter, rejectAfter } from './lib/deadline'
import { track } from './qoe'
import { authenticatedRequestLacksSwipeBearer } from './swipe-bearer-rule'
import { hashesDasSurfaces } from './workers/snapshot-parse-core'
import { parseSnapshotInWorker } from './workers/snapshot-parser-client'

// ── PRAZOS ──────────────────────────────────────────────────────────────────
// Este arquivo é o fetcher que vive FORA do `request()` do `api.ts` (ele faz
// fetch próprio pra medir banda pelo stream). Por isso não herdou o
// AbortController de lá — e a política do `api.ts` já dizia o porquê de ela
// existir: "fetch nativo não tem timeout". O resultado foram 5 `await` sem
// terminação garantida no caminho que alimenta o catálogo inteiro do app; um
// deles pendurado = TV sem catálogo, sem recuperação.
//
// Todo prazo aqui é POR PASSO, não um teto global: um catálogo de 5-15MB em
// vlow pode levar minutos legitimamente, e matar isso seria trocar um bug por
// outro. O que não pode é PARAR e ninguém perceber.

/** Leitura no IDB (`get`). Pode pendurar no evento `blocked` — real em Tizen
 *  2017 com quota apertada. Sem resposta = "não tem cache", que leva à MESMA
 *  decisão (baixar). Fallback honesto. */
const IDB_READ_TIMEOUT_MS = 5_000

/** Escrita/`touch` no IDB. Mais generoso: grava o catálogo inteiro. */
const IDB_WRITE_TIMEOUT_MS = 30_000

/** Resolver o Bearer pode ir à rede (`refreshIfNeeded` / rotação do token
 *  Swipe). Sem header em 12s, SEM sessão Swipe, seguimos sem ele: o backend responde
 *  401 e o erro é TERMINAL — infinitamente melhor que pendurar antes de emitir o
 *  request. COM sessão Swipe o pedido não sai (ver `fetchSnapshot`). */
const AUTH_HEADER_TIMEOUT_MS = 12_000

/** Até os headers da resposta. Mesma política do `request()` do `api.ts`
 *  (15s cobre cold cache + Xtream lento com margem). */
const CONNECT_TIMEOUT_MS = 15_000

/** Maior intervalo tolerado ENTRE DOIS CHUNKS. Re-armado a cada chunk: mede
 *  ESTOL, não lentidão. Download que trickle continua vivo; socket em buraco
 *  negro morre em 20s. */
const STALL_TIMEOUT_MS = 20_000

/** Parse do JSON (worker, com fallback pro main thread). O worker degrada em
 *  erro e em crash, mas SILÊNCIO não tem tratamento — e silêncio é justamente o
 *  que um worker morto produz. Em vlow o parse de 15MB é ~500ms; 30s é ordens de
 *  grandeza acima de qualquer caso legítimo. */
const PARSE_TIMEOUT_MS = 30_000

/**
 * Corpo inteiro no ramo SEM stream (`response.arrayBuffer()`), onde não há chunk
 * pra re-armar nada. Este NÃO pode ser o prazo de estol: ali os 20s deixariam de
 * medir silêncio e passariam a medir o download completo.
 *
 * E o ramo é o do PISO, não um caminho exótico: `fetch` nativo é Chr42+, então
 * no Chr38 quem responde é o `whatwg-fetch` injetado pelo `vite.config.ts`, que
 * **não implementa ReadableStream** (0 ocorrências de `getReader` no pacote
 * 3.6.20). Lá `response.body` nunca é stream e este ramo roda SEMPRE. O Chr53 da
 * LG tem fetch nativo e fica no ramo de cima — foi por isso que passou
 * despercebido.
 *
 * 120s, calibrado contra a régua do próprio projeto: `bandwidth.ts:55` classifica
 * `< 2 Mbps` como `poor` e as fixtures usam ~1 Mbps como representativo. Um
 * payload comprimido de 2MB (o pior caso medido) leva ~16s a 1 Mbps — 120s dá
 * **7,5× de folga** sobre essa referência e cobre até ~0,13 Mbps, abaixo do que
 * um app de vídeo não tem o que fazer de qualquer jeito. Ainda sobra margem pra
 * conversão blob→ArrayBuffer no SoC de 2017, que no polyfill é FileReader.
 *
 * Os dois vizinhos confirmam a ordem de grandeza: é 2,7× o prazo duro do boot
 * (45s, que já libera o gate muito antes, então isto não segura catálogo
 * nenhum), e 1/15 do poll de 30min (1/30 do de 60min em rede `poor`) — um corpo
 * travado sempre termina bem antes da próxima tentativa, sem empilhar.
 */
const BODY_TIMEOUT_MS = 120_000

/**
 * Cargas em voo por provider — dedupe do trecho de REDE.
 *
 * Sem isto, o prazo duro de 45s do boot abre o gate com o download de boot ainda
 * em voo e o `useCatalogRefresh` dispara um SEGUNDO download do MESMO snapshot:
 * 5-15MB × 2 competindo pela banda mais escassa da frota. Pior em vlow/low, onde
 * o boot nem registra `onProgress` e o gate fica aberto de vez. Era exatamente
 * isso que o gate do #553 existia pra evitar — e a resposta certa é resolver no
 * FETCHER (uma verdade só, para todos os callers) em vez de segurar a UI.
 *
 * Bônus: elimina o interleaving de dois `catalogStore.set` no mesmo provider
 * (delete meta → escreve surfaces → escreve meta), que quebrava a invariante que
 * o delete-first protege.
 *
 * O registro é SÍNCRONO, antes de qualquer `await` — senão duas chamadas
 * concorrentes passariam as duas pela leitura do IDB antes de qualquer uma se
 * registrar, e a janela de corrida continuaria aberta.
 *
 * Canto semântico, declarado: um caller com `force: true` que chega enquanto uma
 * carga SEM force está em voo recebe o resultado dela — e, se aquela tiver
 * achado cache fresco, virá `source: 'cache'`. Ninguém recebe dado velho (fresco
 * = dentro do TTL de 24h), e na prática o caso não acontece: quem baixa é
 * justamente quem não tinha cache fresco.
 *
 * ⚠️ ACOPLAMENTO QUE NÃO PODE SER QUEBRADO: este dedupe só é seguro porque TODA
 * etapa de `carregar`/`fetchSnapshot` tem prazo. Uma promise em voo que nunca
 * assentasse não travaria só a si mesma — ela vira a resposta de **toda** chamada
 * seguinte deste provider, congelando o catálogo pela sessão inteira. Seria a
 * classe de bug que esta entrega fecha, reconstruída uma camada abaixo. (Não é
 * teórico: aconteceu no harness destes testes, quando um stream ficou sem
 * `done`.) Quem adicionar um `await` aqui adiciona um prazo junto.
 */
const inflight = new Map<string, Promise<SnapshotResult>>()

export type SnapshotSource = 'cache' | 'fetch' | 'not-modified'

export interface SnapshotResult {
  snapshot: CatalogSnapshot
  source: SnapshotSource
  bandwidthMbps: number | null
  contentHash: string
  /** O que REALMENTE foi parar no IndexedDB.
   *
   *  CONTRATO: presente SEMPRE que `source === 'fetch'` — inclusive quando a
   *  gravação estourou o prazo (`timedOut`) ou o disjuntor não a deixou rodar
   *  (`skipped`). Ausente SÓ em `cache`/`not-modified`, onde o payload já estava
   *  em disco e não houve o que gravar.
   *
   *  ⚠️ Antes, o prazo estourado também devolvia `undefined`, e a tela lia
   *  `undefined` como "o 304 confirmou, sucesso": uma gravação PENDURADA exibia
   *  "X títulos atualizados". Os dois arquivos estavam certos sozinhos; o defeito
   *  morava na fronteira. Agora ausência tem um significado só.
   *
   *  ⚠️ `snapshot` acima é o objeto EM MEMÓRIA, recém-baixado: ele existe mesmo
   *  quando a gravação falhou inteira. Contar `snapshot.vodItems.length` e
   *  chamar de "atualizado" foi exatamente o bug que fez Configurações exibir
   *  "15329 títulos atualizados" com o IDB vazio. Quem for afirmar persistência
   *  pro usuário tem que ler ISTO, não o `snapshot`. */
  write?: SnapshotWriteResult
}

export interface LoadSnapshotOptions {
  /** Bypass cache local e refetch obrigatório. Default false. */
  force?: boolean
  /** TTL pra considerar cache local "fresco". Default 24h. */
  ttlMs?: number
  /** Callback de progresso (bytes baixados). Útil pra UI de splash. */
  onProgress?: (bytes: number, totalEstimated: number) => void
  /** Interno: não envia `If-None-Match` mesmo com cache local. Usado
   *  no retry pós-edge "304 sem cache" pra evitar risco de loop se
   *  outro processo popular o cache entre os dois fetches. */
  _skipIfNoneMatch?: boolean
  /**
   * Tentativa MANUAL ("Atualizar catálogo"). Leva o rearme do disjuntor COM ELA:
   * de `off` grava a prioridade (só nesta tentativa), não passa pelo teto de
   * frequência da prioridade, e o aparelho só sai de `off` se ESTA tentativa
   * chegar ao `set()` e a prioridade entrar. Nenhum poll automático usa isto.
   *
   * Limite declarado: se já houver uma carga automática em voo pro mesmo
   * provedor, o dedupe (`inflight`) devolve a dela — a tentativa manual não
   * acontece e a tela mostra o resultado daquela. Apertar de novo resolve.
   */
  manual?: boolean
}

/**
 * Carrega snapshot do catálogo respeitando cache local + revalidação.
 * Mede banda durante o fetch.
 */
export function loadSnapshot(
  providerId: string,
  opts: LoadSnapshotOptions = {},
): Promise<SnapshotResult> {
  const emVoo = inflight.get(providerId)
  if (emVoo) return emVoo

  const p = carregar(providerId, opts)
  inflight.set(providerId, p)
  const limpar = (): void => {
    // Só apaga a PRÓPRIA entrada: uma carga nova pode já ter registrado a dela.
    if (inflight.get(providerId) === p) inflight.delete(providerId)
  }
  // `.then(ok, err)` e não `.finally` — ES2018 (Chr63+) e o piso é Chr38.
  p.then(limpar, limpar)
  return p
}

async function carregar(
  providerId: string,
  opts: LoadSnapshotOptions,
): Promise<SnapshotResult> {
  const ttl = opts.ttlMs ?? SNAPSHOT_DEFAULT_TTL_MS
  const cached = await orFallbackAfter(catalogStore.get(providerId), IDB_READ_TIMEOUT_MS, null)

  // Hit de cache — usa direto (a menos que force=true)
  if (!opts.force && cached && !catalogStore.isStale(cached, ttl)) {
    return {
      snapshot: cached.snapshot,
      source: 'cache',
      bandwidthMbps: cached.bandwidthMbps,
      contentHash: cached.contentHash,
    }
  }

  return fetchSnapshot(providerId, opts, cached)
}

async function fetchSnapshot(
  providerId: string,
  opts: LoadSnapshotOptions,
  cached: SnapshotRecord | null,
): Promise<SnapshotResult> {
  // Fetch + medir banda
  // Bearer bi-identidade (I3+I4 — fix A1): com sessão Swipe usa o token SWIPE
  // (backend resolve o providerId pela fonte validada); sem sessão, cai no token
  // XTREAM legado, como antes. swipeAuthHeader centraliza a escolha + renova.
  const bearer = await orFallbackAfter(swipeAuthHeader(), AUTH_HEADER_TIMEOUT_MS, null)
  // Com sessão Swipe e sem Bearer, o snapshot (autenticado) não sai: a mesma regra do
  // `request()` do api.ts (swipe-bearer-rule.ts). Falha transitória.
  if (authenticatedRequestLacksSwipeBearer(true, bearer)) {
    throw new Error('snapshot: token da conta ainda não veio (refresh no ar)')
  }
  const headers: Record<string, string> = {
    accept: 'application/json',
    // Browser anuncia compressão suportada — backend escolhe brotli/gzip
    'accept-encoding': 'br, gzip',
  }
  if (bearer) headers['authorization'] = `Bearer ${bearer}`
  // Se já temos snapshot anterior, manda hash pra possível 304.
  // _skipIfNoneMatch é usado no retry pós-edge pra forçar 200.
  if (cached?.contentHash && !opts._skipIfNoneMatch) {
    headers['if-none-match'] = `"${cached.contentHash}"`
  }

  // API_BASE runtime (getApiBase) em vez de build-time — herda o failover
  // entre endpoints como o resto do app (fix A1, sugestão do reviewer).
  const url = `${getApiBase()}/v1/catalog/snapshot`
  const start = performance.now()

  // Um prazo pra TODA a conversa de rede: começa como prazo de CONEXÃO (até os
  // headers) e vira prazo de ESTOL (entre chunks) assim que a resposta chega.
  // Quem dá a garantia é o `race` contra `net.expired`; o `signal` é o bônus
  // que solta o socket (ver `lib/deadline.ts`).
  const net = makeNetDeadline(CONNECT_TIMEOUT_MS, 'catalog/snapshot')

  let bytesRead = 0
  let chunks: Uint8Array[] = []
  let elapsedMs = 0
  let response: Response

  try {
    // `exactOptionalPropertyTypes`: monta o init CONDICIONALMENTE, nunca
    // `{ signal: undefined }`.
    const init: RequestInit = net.signal ? { headers, signal: net.signal } : { headers }
    response = await Promise.race([fetch(url, init), net.expired])
    // Headers na mão: o relógio passa a medir silêncio entre bytes.
    net.bump(STALL_TIMEOUT_MS)

    // 304 Not Modified — usa cache local com novo timestamp
    if (response.status === 304 && cached) {
      net.clear()
      // Renova o storedAt pra "resetar" o TTL (snapshot foi confirmado fresh).
      // touch() só reescreve o meta: o payload já está em disco e não mudou
      // (foi exatamente isso que o 304 confirmou). Antes do split, isto
      // regravava o catálogo inteiro por um timestamp.
      await orFallbackAfter(catalogStore.touch(providerId), IDB_WRITE_TIMEOUT_MS, false)
      return {
        snapshot: cached.snapshot,
        source: 'not-modified',
        bandwidthMbps: cached.bandwidthMbps,
        contentHash: cached.contentHash,
      }
    }

    // Edge: backend confirma 304 mas cache local sumiu (evictado por
    // idb-quota-manager entre o `catalogStore.get` acima e o fetch).
    // Sem este branch, status 304 sem `cached` cairia no `!response.ok`
    // e hook reportaria `result: 'error'` toda vez que eviction
    // agressivo acontecesse entre o get e o fetch. Retry com
    // `_skipIfNoneMatch: true` força 200 com body — anti-loop garantido
    // pela flag (não depende de race com cache local). A chamada recursiva
    // abre o PRÓPRIO prazo; por isso fechamos este antes.
    if (response.status === 304 && !cached) {
      net.clear()
      // Re-entra em `fetchSnapshot`, NÃO em `loadSnapshot`: o dedupe já tem
      // ESTA carga registrada, então voltar pela porta da frente encontraria a
      // própria promise em voo e travaria em deadlock. `cached` vai `null` de
      // propósito (é o que o 304-sem-cache acabou de descobrir).
      return fetchSnapshot(
        providerId,
        { ...opts, force: true, _skipIfNoneMatch: true },
        null,
      )
    }

    if (!response.ok) {
      throw new Error(`snapshot fetch failed: ${response.status}`)
    }

    // Lê body em stream se possível (pra reportar progresso). Fallback arrayBuffer.
    if (response.body && typeof response.body.getReader === 'function') {
      const reader = response.body.getReader()
      while (true) {
        // O `read()` é o `await` que mais pendura na vida real: o servidor
        // aceitou a conexão, mandou os headers e parou de mandar bytes. Sem o
        // race, este `while` espera pra sempre — e a fase do boot nunca vira
        // terminal.
        const { value, done } = await Promise.race([reader.read(), net.expired])
        if (done) break
        if (value) {
          chunks.push(value)
          bytesRead += value.byteLength
          // Chegou byte: o prazo de estol recomeça do zero.
          net.bump(STALL_TIMEOUT_MS)
          opts.onProgress?.(bytesRead, bytesRead) // total não é confiável quando comprimido
        }
      }
    } else {
      // ⚠️ RAMO DO PISO (ver `BODY_TIMEOUT_MS`): sem ReadableStream não há chunk
      // pra re-armar o relógio, então o prazo aqui mede o CORPO INTEIRO e
      // precisa de orçamento próprio. Herdar os 20s de estol matava download
      // legítimo no device mais lento da frota — e matava em TODA tentativa
      // (boot, session-start, poll), porque o teto seria sempre o mesmo:
      // catálogo vazio permanente, o sintoma que esta entrega existe pra
      // consertar, transplantado pra outra população.
      net.bump(BODY_TIMEOUT_MS)
      const buf = await Promise.race([response.arrayBuffer(), net.expired])
      chunks = [new Uint8Array(buf)]
      bytesRead = buf.byteLength
    }

    elapsedMs = performance.now() - start
  } finally {
    // Caminho feliz, `throw` ou prazo estourado: o timer NUNCA fica pendurado.
    net.clear()
  }

  const elapsedSec = Math.max(0.001, elapsedMs / 1000)

  // Combina chunks em um Uint8Array só pra parsing
  const total = bytesRead
  const merged = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.byteLength
  }
  // Parse fora do main thread — em vlow Tizen 2017, decode + parse de
  // 5-15MB JSON trava ~500ms (frame drop visível na splash). Worker
  // recebe o buffer via Transferable (zero-copy) e devolve o objeto.
  // Fallback automático se Worker indisponível.
  // ⚠️ O worker degrada pro main thread quando REPORTA erro ou quando o evento
  // `error` dispara — mas SILÊNCIO (worker vivo que nunca responde) não tem
  // tratamento nenhum, e silêncio é exatamente o que um worker morto produz.
  // Rejeitar é o desfecho certo: vira `catalog_revalidated{result:'error'}` e a
  // fase do boot fecha, em vez de pendurar.
  //
  // Em `priority-only` o worker devolve também o hash da fatia de prioridade,
  // calculado junto do parse, FORA da thread principal (review do `c8268b84`,
  // P2). O modo é o PREVISTO: se mudar até a gravação, `gravarComDisjuntor` usa
  // os hashes só se cobrirem as surfaces do modo de então, e senão calcula ele
  // mesmo (depois do atalho). Em `full` e `off` nada é pedido.
  const modoPrevisto = modoDaTentativa(opts.manual === true)
  const hashSurfaces = modoPrevisto === 'priority-only' ? surfacesFor(modoPrevisto) : null
  const { snapshot, hashes: hashesDoWorker } = await rejectAfter(
    parseSnapshotInWorker<CatalogSnapshot>(merged.buffer, hashSurfaces ? { hashSurfaces } : {}),
    PARSE_TIMEOUT_MS,
    'snapshot-parse',
  )

  // Bandwidth = bytes transferidos (já decomprimidos pelo browser) / tempo total
  // Nota: bytesRead aqui é o tamanho APÓS decompressão. Pra ser fiel ao real
  // transfer-size precisaríamos do Resource Timing API. Aproximação aceitável
  // — em catálogos com padding aleatório, compressão não muda muito o tamanho.
  const mbps = (bytesRead * 8) / 1_000_000 / elapsedSec

  // Hash do conteúdo: extraímos do header ETag (mesmo cálculo do backend)
  const etag = response.headers.get('etag') ?? ''
  const contentHash = etag.replace(/"/g, '')

  // Disjuntor, pulo por conteúdo igual e prazo da gravação: ver
  // `gravarComDisjuntor`. O retorno é SEMPRE um resultado (nunca `undefined`).
  const write = await gravarComDisjuntor(
    providerId,
    contentHash,
    snapshot,
    mbps,
    opts.manual === true,
    hashesDoWorker,
  )

  return {
    snapshot,
    source: 'fetch',
    bandwidthMbps: mbps,
    contentHash,
    write,
  }
}

/**
 * O disco guarda o MESMO ARTEFATO do servidor? É o ATALHO barato antes de
 * qualquer hash: se sim, pular é seguro em qualquer modo, e a thread principal
 * não hasheia nada (o worker, se funcionou, já pode ter hasheado junto do parse,
 * fora da thread da UI).
 *
 * ⚠️ NÃO é o que decide em `priority-only` — lá quem decide é o hash da própria
 * fatia (`hashesDaFatia`). Ver "Correção" abaixo.
 *
 * ── Por que não basta o hash do ETag ──
 * O hash vem do header ETag. Numa origem cruzada ele só é legível se a API o
 * expuser (`Access-Control-Expose-Headers`); sem isso `headers.get('etag')` é
 * `null` e o hash vira `''` — e `''` não prova nada. É o caso da TV (`file://`
 * → API https). Com a comparação só por hash, o pulo NUNCA disparava lá, e em
 * `priority-only` cada abertura e cada poll de 30 min regravava os ~400 KB do
 * caminho do vídeo: a sangria que o disjuntor existe pra parar, por outra porta.
 *
 * ── A chave de reserva: `generatedAt` ──
 * O backend carimba `generatedAt` quando CONSTRÓI o snapshot e serve o artefato
 * do cache por até 24h (apps/api `catalog-service.ts`; o hash do ETag exclui o
 * `generatedAt` justamente por isso). Então:
 *  • mesmo `generatedAt` ⇒ mesmo artefato ⇒ mesmo conteúdo. Pular é seguro (a
 *    rota do snapshot serve o artefato do cache sem alterá-lo).
 *  • `generatedAt` diferente NÃO prova conteúdo diferente (o servidor pode ter
 *    reconstruído igual).
 * Risco residual, declarado: duas reconstruções do MESMO provedor no MESMO
 * segundo, com conteúdos diferentes, fariam a segunda ser pulada até a próxima
 * reconstrução.
 *
 * ── Correção (review do `181b1f81`): "~1 regravação por dia" era FALSO ──
 * Esta docblock afirmava que um `generatedAt` diferente custava "~1 regravação
 * por dia". O snapshot se reconstrói bem mais: a cada 5 min quando degradado, a
 * cada bump de `cat:ver` (escopo compartilhado) e, no HXTV, a cada ciclo do
 * `live_streams` — até ~48 por dia. Comparar a prioridade por `generatedAt`
 * regravava ~400 KB a cada poll até o aparelho cair em `off`. Por isso ele virou
 * só o atalho, e em `priority-only` a decisão é o hash da fatia, com teto de
 * frequência (PRIORITY_REWRITE_MIN_INTERVAL_MS).
 *
 * Com os dois hashes legíveis, o hash manda: é a invariante de conteúdo, e a
 * reserva só entra quando ela não existe.
 */
export function mesmoConteudo(
  meta: { contentHash: string; generatedAt: number },
  contentHash: string,
  snapshot: { generatedAt?: unknown },
): boolean {
  if (contentHash !== '' && meta.contentHash !== '') return meta.contentHash === contentHash
  const g = snapshot.generatedAt
  return typeof g === 'number' && g > 0 && meta.generatedAt === g
}

/**
 * Intervalo MÍNIMO entre regravações da prioridade em `priority-only`, quando
 * ela muda de verdade.
 *
 * ── Por que precisa existir ──
 * O hash da fatia resolve "o servidor reconstruiu com a prioridade igual". Não
 * resolve a prioridade que MUDA a cada ciclo: o `live_streams` do HXTV pode vir
 * diferente a cada reconstrução (até ~48/dia). Sem teto, seriam ~48 × 400 KB de
 * gravação por dia — num aparelho em que cada gravação come a capacidade que
 * favoritos e progresso precisam.
 *
 * ── O número, contra o TTL ──
 * 6 h = 1/4 do TTL do cache (SNAPSHOT_DEFAULT_TTL_MS, 24 h). A fatia nunca fica
 * mais de 6 h atrás do servidor — bem dentro da janela em que o próprio cache já
 * aceita servir dado; o `touch()` de meio-TTL mantém o meta válido no meio tempo.
 * Teto: 4 gravações/dia, contra até ~48 (12× menos). O dado local um pouco mais
 * velho é aceitável porque o caminho do vídeo re-deriva a fonte do catálogo
 * vivo a cada clique: canal velho no cache não vira URL morta.
 *
 * Descartados: 1 h (até 24/dia, pouco ganho sobre o problema); 24 h (igual ao
 * TTL — a fatia poderia ficar um dia inteiro atrás, e canal novo sumiria da
 * lista por tempo demais). "Atualizar catálogo" (manual) não passa por este teto.
 *
 * ── Desde QUANDO (review do `c8268b84`, P2) ──
 * Duas memórias, e o teto segura se QUALQUER uma disser "recente" (`dentroDoTeto`):
 *  • o `writtenAt` do meta (IndexedDB), gravado quando a fatia entra;
 *  • a última TENTATIVA deste provedor (localStorage, catalog-write-breaker),
 *    registrada ANTES do `set()`. Sem ela, gravação cujo meta não entra (erro
 *    que não é cota, prazo estourado depois do `delete meta`) não deixava rastro
 *    e repetia a cada poll: 10 polls, 10 `set()`. Por isso o teto vale também
 *    com o meta AUSENTE ou incompleto, não só com a fatia inteira em disco.
 * Instante no FUTURO conta como vencido: relógio da TV que estava adiantado e
 * foi corrigido seguraria o teto pela diferença + 6 h (sonda com 30 dias:
 * nenhuma gravação em 7 dias). Uma gravação depois da correção carimba o
 * instante certo, e o teto volta a valer.
 */
export const PRIORITY_REWRITE_MIN_INTERVAL_MS = 6 * 60 * 60 * 1000

/**
 * Algum dos instantes (ms; `null` = não há) cai dentro do teto? Instante no
 * futuro NÃO segura (ver "Desde QUANDO" acima). Pura, pra ser testável direto.
 */
export function dentroDoTeto(agora: number, instantes: ReadonlyArray<number | null>): boolean {
  return instantes.some(
    (t) => t !== null && agora >= t && agora - t < PRIORITY_REWRITE_MIN_INTERVAL_MS,
  )
}

/** Gravações reais e pulos nesta sessão — pra telemetria medir a sangria. */
let gravacoesSessao = 0
let pulosSessao = 0

/** O modo em que ESTA tentativa roda: a manual tem o dela (ver o disjuntor). */
function modoDaTentativa(manual: boolean): CatalogWriteMode {
  return manual ? modoDaTentativaManual() : catalogWriteMode()
}

/**
 * Hash de conteúdo de cada surface da fatia.
 *
 * Os do WORKER, quando cobrem todas as surfaces pedidas (calculados junto do
 * parse, fora da thread principal). Senão — sem worker, worker que falhou, ou
 * modo que mudou entre o parse e a gravação — calcula AQUI, na thread
 * principal, com a mesma função do worker (`snapshot-parse-core.ts`). Quem
 * chama só chega aqui DEPOIS do atalho do `mesmoConteudo`.
 */
function hashesDaFatia(
  snapshot: CatalogSnapshot,
  surfaces: readonly SurfaceName[],
  doWorker: Record<string, string> | null,
): Partial<Record<SurfaceName, string>> {
  if (doWorker !== null) {
    const out: Partial<Record<SurfaceName, string>> = {}
    let cobre = true
    for (const s of surfaces) {
      const h = doWorker[s]
      if (typeof h !== 'string') {
        cobre = false
        break
      }
      out[s] = h
    }
    if (cobre) return out
  }
  return hashesDasSurfaces(snapshot as unknown as Record<string, unknown>, surfaces)
}

/** Resultado de um pulo: o que o meta diz que está em disco continua lá. Sem
 *  meta, nada está legível (o `set()` só valida o conjunto pelo meta). */
function resultadoDoDisco(
  meta: SnapshotMeta | null,
  motivo: 'unchanged' | 'throttled',
): SnapshotWriteResult {
  if (meta === null) return nadaGravado({ skipped: motivo })
  const persisted = SURFACE_NAMES.filter((s) => meta.surfaces.includes(s))
  return {
    ok: persisted.length === SURFACE_NAMES.length,
    persisted,
    failed: SURFACE_NAMES.filter((s) => !meta.surfaces.includes(s)),
    quotaExceeded: false,
    unavailable: false,
    skipped: motivo,
  }
}

/**
 * Registra o pulo (telemetria) e, com a fatia em disco, renova o meta se
 * preciso. O leitor quente (`readSurface`, api.ts) descarta meta mais velho que
 * o TTL: sem renovar, pular aposentaria o cache de canais em 24 h. Renova no
 * máximo uma vez por MEIO TTL: ~2 escritas de meta (bytes) por dia.
 */
async function pular(
  motivo: 'breaker-off' | 'unchanged' | 'throttled',
  modo: string,
  resultado: SnapshotWriteResult,
  providerId?: string,
  meta?: SnapshotMeta,
): Promise<SnapshotWriteResult> {
  pulosSessao += 1
  track({
    type: 'snapshot_write_skipped',
    ts: Math.floor(Date.now() / 1000),
    motivo,
    modo,
    pulosSessao,
    tier: getCapability('cpuTier'),
  })
  if (providerId && meta && Date.now() - meta.storedAt * 1000 > SNAPSHOT_DEFAULT_TTL_MS / 2) {
    await orFallbackAfter(catalogStore.touch(providerId), IDB_WRITE_TIMEOUT_MS, false)
  }
  return resultado
}

/** Nada foi gravado — base dos resultados que não chegam ao `set()`. */
function nadaGravado(extra: Pick<SnapshotWriteResult, 'skipped' | 'timedOut'>): SnapshotWriteResult {
  return {
    ok: false,
    persisted: [],
    failed: [...SURFACE_NAMES],
    quotaExceeded: false,
    unavailable: false,
    ...extra,
  }
}

/**
 * Grava o snapshot recém-baixado PASSANDO PELO DISJUNTOR (catalog-write-breaker).
 *
 * Saídas antes de tocar o disco, em ordem de custo:
 *  1. disjuntor em `off`: não grava nada. O aparelho não comporta nem a
 *     prioridade, e tentar custa capacidade mesmo quando falha.
 *  2. ATALHO, sem hash nenhum: o disco tem a fatia e é o MESMO artefato do
 *     servidor (`mesmoConteudo`). Sem isto o `priority-only` não pararia a
 *     sangria — o `get()` completo devolve `null` pra snapshot parcial, então
 *     toda revalidação vem sem ETag, recebe 200 e o `set()` APAGARIA e
 *     regravaria `liveChannels` a cada 30 minutos.
 *  3. Em `priority-only`, o hash da PRÓPRIA FATIA igual ao do disco: não regrava.
 *  4. Em `priority-only`, teto de frequência (`dentroDoTeto`), com ou sem meta.
 *  5. grava só as surfaces que o modo permite, e o disjuntor aprende com o
 *     resultado (só desce; ver o módulo).
 *
 * ⚠️ Ordem (review do `c8268b84`, P2): o hash da fatia só é calculado DEPOIS do
 * atalho, e só se o worker não o trouxe. Antes ele rodava primeiro, e um pulo
 * pelo atalho custava 4 `hashConteudo` (~460 KB de JSON) na thread principal
 * pra nada.
 *
 * Nunca devolve `undefined`: ver o contrato de `SnapshotResult.write`.
 */
async function gravarComDisjuntor(
  providerId: string,
  contentHash: string,
  snapshot: CatalogSnapshot,
  mbps: number,
  manual: boolean,
  hashesDoWorker: Record<string, string> | null,
): Promise<SnapshotWriteResult> {
  // A tentativa manual roda num modo PRÓPRIO (de `off`, a prioridade), sem mudar
  // o modo do aparelho — o rearme só é consumido lá embaixo, se chegar ao `set()`.
  const modo = modoDaTentativa(manual)
  const permitidas = surfacesFor(modo)
  if (permitidas === null) return pular('breaker-off', modo, nadaGravado({ skipped: 'breaker-off' }))

  const meta = await orFallbackAfter(catalogStore.getMeta(providerId), IDB_READ_TIMEOUT_MS, null)
  const fatiaEmDisco = meta !== null && permitidas.every((s) => meta.surfaces.includes(s))

  // 2. Atalho barato: MESMO artefato do servidor (mesmo `generatedAt` / hash do
  //    ETag) ⇒ mesmo conteúdo. Vale nos dois modos, e vem ANTES de qualquer hash.
  if (meta && fatiaEmDisco && mesmoConteudo(meta, contentHash, snapshot)) {
    return pular('unchanged', modo, resultadoDoDisco(meta, 'unchanged'), providerId, meta)
  }

  // Em `priority-only` a fatia é pequena (~400 KB) e o hash dela é o que decide.
  // Em `full`, hashear 5-15 MB a cada poll custaria mais do que poupa num
  // aparelho que TEM espaço — ali segue o comportamento anterior.
  // Calculado só quando alguém precisa dele (comparar ou gravar), uma vez.
  const prioridade = modo === 'priority-only'
  let hashesCalculados: Partial<Record<SurfaceName, string>> | null = null
  const hashes = (): Partial<Record<SurfaceName, string>> => {
    if (hashesCalculados === null) hashesCalculados = hashesDaFatia(snapshot, permitidas, hashesDoWorker)
    return hashesCalculados
  }

  // 3. O `generatedAt` muda a cada reconstrução do snapshot no servidor — até
  //    ~48/dia no HXTV — com a prioridade idêntica; o ETag é do snapshot
  //    inteiro. Nenhum dos dois responde "a prioridade mudou?"; o hash da fatia
  //    responde.
  if (prioridade && meta && fatiaEmDisco) {
    const h = hashes()
    if (permitidas.every((s) => meta.hashes?.[s] === h[s])) {
      return pular('unchanged', modo, resultadoDoDisco(meta, 'unchanged'), providerId, meta)
    }
  }

  // 4. Mudou DE VERDADE (ou não há fatia inteira em disco), em `priority-only`:
  //    no máximo uma gravação por PRIORITY_REWRITE_MIN_INTERVAL_MS, contada do
  //    `writtenAt` do meta OU da última tentativa (que cobre o meta que não
  //    entrou e o prazo estourado). O `live_streams` do HXTV pode mudar a cada
  //    ciclo, e cada gravação custa capacidade no aparelho sob pressão.
  //    A tentativa MANUAL não passa por este teto: o usuário pediu o catálogo
  //    atual, e é UMA gravação deliberada.
  if (prioridade && !manual) {
    const gravadoEm = meta !== null && typeof meta.writtenAt === 'number' ? meta.writtenAt * 1000 : null
    if (dentroDoTeto(Date.now(), [gravadoEm, ultimaTentativa(providerId)])) {
      return pular('throttled', modo, resultadoDoDisco(meta, 'throttled'), providerId, meta ?? undefined)
    }
  }

  const motivo = manual
    ? 'manual'
    : !fatiaEmDisco
      ? 'ausente'
      : prioridade
        ? 'intervalo'
        : 'mudou'
  gravacoesSessao += 1
  const hashesParaGravar = prioridade ? hashes() : null
  // ANTES do `set()`: o prazo estourado nunca devolve nada, e é justamente o
  // caso que precisa deixar rastro pro teto (ver catalog-write-breaker).
  registrarTentativa(providerId, Date.now())

  // Prazo na GRAVAÇÃO fecha um vazamento que não é do boot: o poll de 30min do
  // `useCatalogRefresh` é encadeado (`await revalidate(); schedulePoll()`), então
  // um `set` pendurado no IDB mataria o polling da sessão inteira.
  //
  // Estourou? O desfecho é DESCONHECIDO — a gravação em voo pode completar
  // depois, e quem decide isso é o próximo `getMeta`, lendo o disco. Por isso
  // `timedOut` (nunca `undefined`, que a tela lia como sucesso) e o disjuntor
  // NÃO aprende nada: não houve resultado pra aprender.
  const r = await orFallbackAfter<SnapshotWriteResult | null>(
    catalogStore.set(providerId, contentHash, snapshot, mbps, {
      surfaces: permitidas,
      ...(hashesParaGravar ? { hashes: hashesParaGravar } : {}),
      telemetria: { motivo, modo, gravacoesSessao },
    }),
    IDB_WRITE_TIMEOUT_MS,
    null,
  )
  if (r === null) return nadaGravado({ timedOut: true })
  // Só aqui — a tentativa CHEGOU ao `set()` e voltou — o rearme manual é
  // consumido. O automático só aprende pra baixo.
  const depois = manual ? registrarResultadoManual(r) : noteWriteResult(r)
  // Caiu (ou continua) em `off`: a AUTO-CURA da cota apaga o `iptv-catalog` (uma
  // vez por episódio; catalog-auto-cura.ts) sem esperar o próximo boot — os
  // outros escritores (favoritos, progresso, outbox) pegam a cota de volta já.
  // Sem `await`: quem pediu a gravação não espera o delete.
  if (depois === 'off') void curarCotaSePreciso('transicao').catch(() => 'erro')
  return r
}

/** Limpa snapshot de um provider (ou todos). Usa em logout/troca de provider. */
export async function clearSnapshot(providerId?: string): Promise<void> {
  await catalogStore.clear(providerId)
}
